import { GetPresignedUrl, UploadImageToS3 } from '../services/service';

const loadImageFromUrl = (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => {
      fetch(url)
        .then((response) => {
          if (!response.ok) {
            throw new Error('Failed to fetch image');
          }
          return response.blob();
        })
        .then((blob) => {
          const objectUrl = URL.createObjectURL(blob);
          const fallbackImg = new Image();
          fallbackImg.onload = () => {
            URL.revokeObjectURL(objectUrl);
            resolve(fallbackImg);
          };
          fallbackImg.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            reject(new Error('Failed to load image'));
          };
          fallbackImg.src = objectUrl;
        })
        .catch(reject);
    };
    img.src = url;
  });
};

const rotateImageOnCanvas = (
  img: HTMLImageElement,
  degrees: number,
): HTMLCanvasElement => {
  const normalizedDegrees = ((degrees % 360) + 360) % 360;
  const rad = (normalizedDegrees * Math.PI) / 180;
  const isSideways = normalizedDegrees % 180 !== 0;

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  canvas.width = isSideways ? img.height : img.width;
  canvas.height = isSideways ? img.width : img.height;

  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate(rad);
  ctx.drawImage(img, -img.width / 2, -img.height / 2);

  return canvas;
};

const canvasToFile = (
  canvas: HTMLCanvasElement,
  fileName: string,
  mimeType: string,
): Promise<File> => {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to create image blob'));
          return;
        }
        resolve(new File([blob], fileName, { type: mimeType }));
      },
      mimeType,
      0.92,
    );
  });
};

const getMimeTypeFromUrl = (url: string): { mimeType: string; extension: string } => {
  const match = url.match(/\.(jpe?g|png)(\?|$)/i);
  if (match?.[1]?.toLowerCase() === 'png') {
    return { mimeType: 'image/png', extension: 'png' };
  }
  return { mimeType: 'image/jpeg', extension: 'jpg' };
};

export const rotateImageAndUpload = async (
  imageUrl: string,
  degrees: number,
  folderType: number,
): Promise<string> => {
  const normalizedDegrees = ((degrees % 360) + 360) % 360;
  if (normalizedDegrees === 0) {
    return imageUrl;
  }

  const img = await loadImageFromUrl(imageUrl);
  const canvas = rotateImageOnCanvas(img, normalizedDegrees);
  const { mimeType, extension } = getMimeTypeFromUrl(imageUrl);
  const fileName = `rotated-${Date.now()}.${extension}`;
  const file = await canvasToFile(canvas, fileName, mimeType);
  const presignedUrl = await GetPresignedUrl(fileName, mimeType, folderType);

  return UploadImageToS3(file, presignedUrl);
};
